import { defineNuxtPlugin } from '#app';
import {
    setRenewalInsightProvider,
    type RenewalInsight,
    type RenewalInsightKind,
} from '~~/codepresso-renewal/composables/useRenewalInsights';
import { useHomeService } from '~/composables/service/useHomeService';
import type { ArticleModel } from '~~/package/mapper/home/model/ArticleModel';

/**
 * renewal 콘텐츠의 "최근 소식 / 인사이트" 를 우리 뉴스 API 로 연결한다.
 *
 * renewal 은 단독으로 뜰 때 자체 서버 라우트(`/api/insights`)를 쓰는데, 그 라우트는
 * 우리 앱에 없다. 그대로 두면 목록이 조용히 비거나 깨진다(실제로 빈 카드가 그려졌다).
 * 마크업은 renewal 이 갖고 데이터만 우리가 준다 — renewal 의 DOM·CSS 는 건드리지 않는다.
 *
 * 어떤 종류를 섞을지는 부르는 쪽이 정한다 — GNB 의 "최근 소식" 은 뉴스와 이벤트를
 * 함께(기본 항목이 뉴스 2 + 이벤트 1 구성이었다), 홈 하단 "최근 인사이트" 는 뉴스만.
 */

/** renewal 의 종류 이름을 우리 기사 API 의 label 로 바꾼다. */
export const articleTypesFor = (kinds: readonly RenewalInsightKind[]): string[] =>
    kinds.map((kind) => (kind === 'news' ? 'NEWS' : 'EVENT'));

/** 뱃지에 쓰는 짧은 분류 이름.
 *
 *  `package/locales` 에 넣지 않은 이유: 그 파일들은 번역 스프레드시트에서 통째로
 *  생성되므로(`npm run generate:locales`) 손으로 넣은 키는 다음 동기화에 사라진다.
 *  스프레드시트에 항목이 추가되면 그때 여기를 t() 호출로 바꾸면 된다. */
const CATEGORY_LABEL: Record<string, Record<string, string>> = {
    NEWS: { ko: '뉴스', en: 'News', ja: 'ニュース', vi: 'Tin tức', es: 'Noticias' },
    EVENT: { ko: '이벤트', en: 'Events', ja: 'イベント', vi: 'Sự kiện', es: 'Eventos' },
};

const categoryLabel = (label: string, locale: string): string =>
    CATEGORY_LABEL[label]?.[locale] ?? CATEGORY_LABEL[label]?.en ?? label;

const toInsight = (article: ArticleModel, locale: string): RenewalInsight => ({
    url: article.link,
    title: article.title,
    category: categoryLabel(article.label, locale),
    date: article.date,
    image: article.imageUrl,
});

export default defineNuxtPlugin((nuxtApp) => {
    const homeService = useHomeService();

    setRenewalInsightProvider(async (limit, kinds) => {
        const locale = String((nuxtApp.$i18n as { locale?: { value?: string } })?.locale?.value ?? 'en');
        const lang = locale.toUpperCase();

        // 한쪽이 비거나 실패해도 나머지는 보여 준다.
        const pages = await Promise.allSettled(
            articleTypesFor(kinds).map((type) => homeService.getArticleList(1, limit, type, lang)),
        );

        for (const result of pages) {
            // allSettled 는 실패를 삼킨다. 목록이 조용히 비면 원인을 못 찾으므로 남긴다.
            if (result.status === 'rejected') console.error('[renewal] 기사 목록 조회 실패', result.reason);
        }

        return pages
            .flatMap((result) => (result.status === 'fulfilled' ? result.value.data : []))
            .filter((article) => article?.link && article?.title)
            .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
            .slice(0, limit)
            .map((article) => toInsight(article, locale));
    });
});
